/**
 * Voice from somewhere else: OpenAI's, or your own endpoint.
 *
 * Both work the same way — record until they stop talking, post the audio, get
 * words back — so they are one file with two sets of addresses. Whisper is
 * markedly better than the browser at Hebrew and at an elderly voice, which is
 * the case for having this at all; the browser stays the default because it is
 * free and private.
 *
 * The custom endpoint contract, again small enough to implement in an afternoon:
 *
 *   POST <voiceBaseUrl>/transcribe   multipart form, field `file`, field `lang`
 *   ← { "text": "what they said" }
 *
 *   POST <voiceBaseUrl>/speak        { "text": …, "lang": …, "voice": … }
 *   ← audio bytes (any type the browser can play)
 */
import type { Lang } from '../../domain/types';
import { bcp47, recordOnce, recordingName, type ListenHandlers, type ListenOptions, type Listening, type SpeechToText, type TextToSpeech } from './types';

interface RemoteOpts {
  apiKey: string;
  baseUrl: string;
  /** OpenAI's own paths, or a custom endpoint's. */
  flavour: 'openai' | 'proxy';
  /** Transcription model, for OpenAI. */
  sttModel?: string;
  ttsModel?: string;
}

function urls(o: RemoteOpts) {
  const base = o.baseUrl.trim().replace(/\/+$/, '') || 'https://api.openai.com/v1';
  return o.flavour === 'openai'
    ? { transcribe: `${base}/audio/transcriptions`, speak: `${base}/audio/speech` }
    : { transcribe: `${base}/transcribe`, speak: `${base}/speak` };
}

export function createRemoteStt(o: RemoteOpts): SpeechToText {
  return {
    id: o.flavour,
    // Not live: there is nothing to show until the recording has been sent.
    live: false,
    available: () => (o.flavour === 'proxy' ? !!o.baseUrl.trim() : !!o.apiKey.trim()),
    // Always records until stopped, so `continuous` is already how it behaves.
    async start(lang: Lang, handlers: ListenHandlers, opts: ListenOptions = {}): Promise<Listening> {
      return recordOnce(async (audio) => {
        const form = new FormData();
        form.append('file', audio, recordingName(audio));
        form.append('lang', lang);
        if (o.flavour === 'openai') {
          form.append('model', o.sttModel || 'whisper-1');
          form.append('language', lang);
        }
        const headers: Record<string, string> = {};
        if (o.apiKey.trim()) headers.authorization = `Bearer ${o.apiKey.trim()}`;
        try {
          const res = await fetch(urls(o).transcribe, { method: 'POST', headers, body: form });
          if (!res.ok) {
            handlers.onError('assistant.voice.failed');
            return;
          }
          const data = (await res.json()) as { text?: string };
          handlers.onFinal((data.text ?? '').trim());
        } catch {
          handlers.onError('assistant.voice.failed');
        }
      }, handlers.onError, handlers.onSound, opts.maxMs);
    },
  };
}

export function createRemoteTts(o: RemoteOpts): TextToSpeech {
  let playing: HTMLAudioElement | null = null;
  return {
    id: o.flavour,
    available: () => (o.flavour === 'proxy' ? !!o.baseUrl.trim() : !!o.apiKey.trim()),
    async voices() {
      // OpenAI's list is fixed; a custom endpoint decides for itself, so nothing is claimed for it.
      return o.flavour === 'openai'
        ? ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'].map((v) => ({ id: v, name: v }))
        : [];
    },
    async speak(text: string, lang: Lang, voiceName?: string) {
      if (!text.trim()) return;
      this.stop();
      const headers: Record<string, string> = { 'content-type': 'application/json' };
      if (o.apiKey.trim()) headers.authorization = `Bearer ${o.apiKey.trim()}`;
      const body = o.flavour === 'openai'
        ? { model: o.ttsModel || 'tts-1', input: text, voice: voiceName || 'nova' }
        : { text, lang: bcp47(lang), voice: voiceName || '' };
      // Thrown rather than swallowed: the layer above falls back to the
      // browser's own voice, so a service that is down or blocked costs a plainer
      // voice rather than a silent assistant.
      const res = await fetch(urls(o).speak, { method: 'POST', headers, body: JSON.stringify(body) });
      if (!res.ok) throw new Error(`${o.flavour} tts ${res.status}`);
      const url = URL.createObjectURL(await res.blob());
      const audio = new Audio(url);
      playing = audio;
      try {
        await new Promise<void>((resolve, reject) => {
          audio.onended = () => resolve();
          audio.onerror = () => reject(new Error('playback failed'));
          void audio.play().catch(reject);
        });
      } finally {
        URL.revokeObjectURL(url);
        if (playing === audio) playing = null;
      }
    },
    stop() {
      playing?.pause();
      playing = null;
    },
  };
}
